import React, { useState, useRef } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const MODE_LABELS = {
  'text-to-game': 'Game',
  'prompt-to-mini-game': 'Mini Game',
  'text-to-animation': 'Animation',
  'script-to-video': 'Video',
  'music-to-video': 'Music Video',
};

const MODE_ICONS = {
  'text-to-game': 'Gamepad2',
  'prompt-to-mini-game': 'Zap',
  'text-to-animation': 'Film',
  'script-to-video': 'Video',
  'music-to-video': 'Music',
};

const MODE_COLORS = {
  'text-to-game': 'var(--color-primary)',
  'prompt-to-mini-game': 'var(--color-secondary)',
  'text-to-animation': 'var(--color-accent)',
  'script-to-video': '#10B981',
  'music-to-video': '#F43F5E',
};

export default function OutputPreviewPanel({ game, onPlay, onShare, onRemix, onClose }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [copied, setCopied] = useState(false);
  const videoRef = useRef(null);

  if (!game) return null;

  const modeColor = MODE_COLORS?.[game?.mode] || 'var(--color-primary)';
  const modeLabel = MODE_LABELS?.[game?.mode] || 'Creation';
  const modeIcon = MODE_ICONS?.[game?.mode] || 'Wand2';
  const isGameType = game?.mode === 'text-to-game' || game?.mode === 'prompt-to-mini-game';

  const handleCopyLink = async () => {
    const link = `${window.location?.origin}/game-player-screen?id=${game?.id}`;
    try {
      await navigator.clipboard?.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = game?.thumbnail || '';
    link.download = `${game?.title || 'creation'}.png`;
    link?.click();
  };

  return (
    <div className="rounded-2xl overflow-hidden animate-fade-in-up" style={{ background: 'var(--color-card)', border: `1px solid ${modeColor}40`, boxShadow: `0 0 0 1px ${modeColor}20, var(--shadow-lg)` }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--color-border)', background: `${modeColor}08` }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: modeColor }}>
            <Icon name={modeIcon} size={14} color="white" />
          </div>
          <div>
            <p className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
              {game?.title}
            </p>
            <p className="text-xs" style={{ color: modeColor, fontFamily: 'var(--font-caption)' }}>
              {modeLabel} · Ready to preview
            </p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[var(--color-muted)] transition-all">
            <Icon name="X" size={14} color="var(--color-muted-foreground)" />
          </button>
        )}
      </div>
      {/* Preview area */}
      <div className="relative" style={{ background: '#0a0a0f' }}>
        {isGameType ? (
          <div className="relative h-48 flex items-center justify-center overflow-hidden">
            <Image src={game?.thumbnail} alt={game?.thumbnailAlt || game?.title} className="w-full h-full object-cover opacity-70" />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: modeColor, boxShadow: `0 0 24px ${modeColor}80` }}>
                <Icon name="Play" size={24} color="white" />
              </div>
              <p className="text-xs font-semibold text-white/80" style={{ fontFamily: 'var(--font-caption)' }}>Click Play to launch game</p>
            </div>
          </div>
        ) : (
          <div className="relative h-48 flex items-center justify-center overflow-hidden">
            <Image src={game?.thumbnail} alt={game?.thumbnailAlt || game?.title} className="w-full h-full object-cover opacity-80" />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2" style={{ background: 'rgba(0,0,0,0.35)' }}>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="w-14 h-14 rounded-full flex items-center justify-center transition-transform hover:scale-105"
                style={{ background: modeColor, boxShadow: `0 0 24px ${modeColor}80` }}
              >
                <Icon name={isPlaying ? 'Pause' : 'Play'} size={24} color="white" />
              </button>
              <p className="text-xs font-semibold text-white/80" style={{ fontFamily: 'var(--font-caption)' }}>
                {isPlaying ? 'Playing preview...' : 'Preview your creation'}
              </p>
            </div>
            {/* Simulated progress bar when playing */}
            {isPlaying && (
              <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: 'rgba(255,255,255,0.2)' }}>
                <div className="h-full animate-shimmer" style={{ background: modeColor, width: '60%' }} />
              </div>
            )}
          </div>
        )}

        {/* Music visualizer bars for music-to-video */}
        {game?.mode === 'music-to-video' && (
          <div className="absolute bottom-2 left-0 right-0 flex items-end justify-center gap-0.5 px-4">
            {Array.from({ length: 24 })?.map((_, i) => (
              <div
                key={i}
                className="rounded-full"
                style={{
                  width: 3,
                  height: isPlaying ? `${8 + Math.sin(i * 0.8) * 12 + Math.random() * 8}px` : `${4 + Math.sin(i * 0.5) * 6}px`,
                  background: `${modeColor}${isPlaying ? 'cc' : '60'}`,
                  transition: 'height 0.15s ease',
                }}
              />
            ))}
          </div>
        )}
      </div>
      {/* Generation success badge */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--color-border)', background: 'rgba(16,185,129,0.05)' }}>
        <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(16,185,129,0.15)' }}>
          <Icon name="CheckCircle2" size={12} color="var(--color-success)" />
        </div>
        <p className="text-xs font-medium" style={{ color: 'var(--color-success)', fontFamily: 'var(--font-caption)' }}>
          Generation complete! Your {modeLabel?.toLowerCase()} is ready.
        </p>
      </div>
      {/* Action buttons */}
      <div className="p-3 flex flex-col gap-2">
        {/* Primary action */}
        <button
          onClick={() => onPlay?.(game)}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90 active:scale-[0.98]"
          style={{ background: modeColor, color: 'white', fontFamily: 'var(--font-heading)' }}
        >
          <Icon name={isGameType ? 'Play' : 'PlayCircle'} size={16} color="white" />
          {isGameType ? 'Play Game' : 'Preview'}
        </button>

        {/* Secondary actions */}
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={handleDownload}
            className="flex flex-col items-center gap-1 py-2 rounded-xl border text-xs font-medium transition-all hover:opacity-80"
            style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)', color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}
          >
            <Icon name="Download" size={14} color="var(--color-muted-foreground)" />
            Download
          </button>
          <button
            onClick={handleCopyLink}
            className="flex flex-col items-center gap-1 py-2 rounded-xl border text-xs font-medium transition-all hover:opacity-80"
            style={{
              borderColor: copied ? 'var(--color-success)' : 'var(--color-border)',
              background: copied ? 'rgba(16,185,129,0.08)' : 'var(--color-muted)',
              color: copied ? 'var(--color-success)' : 'var(--color-foreground)',
              fontFamily: 'var(--font-caption)',
            }}
          >
            <Icon name={copied ? 'Check' : 'Share2'} size={14} color={copied ? 'var(--color-success)' : 'var(--color-muted-foreground)'} />
            {copied ? 'Copied!' : 'Share'}
          </button>
          <button
            onClick={() => onRemix?.(game)}
            className="flex flex-col items-center gap-1 py-2 rounded-xl border text-xs font-medium transition-all hover:opacity-80"
            style={{ borderColor: `${modeColor}40`, background: `${modeColor}08`, color: modeColor, fontFamily: 'var(--font-caption)' }}
          >
            <Icon name="Shuffle" size={14} color={modeColor} />
            Remix
          </button>
        </div>
      </div>
    </div>
  );
}
